// Active ragdoll: rigid bodies + joints. Pose-holding is done by Character via
// per-bone PD orientation torques (Rapier's spherical joints have no motors in 0.14;
// only revolute/prismatic UnitImpulseJoints expose configureMotor*). Elbows & knees
// are revolute joints with motors. All other joints (shoulders/hips/spine/neck) are
// passive spherical joints, and the Character drives the bones directly.

import RAPIER from '@dimforge/rapier3d-compat';
import { CONFIG } from '../config';

export enum Bone {
  Pelvis = 0,
  Torso = 1,
  Head = 2,
  UpperArmL = 3,
  LowerArmL = 4,
  UpperArmR = 5,
  LowerArmR = 6,
  LowerLegL = 7,
  LowerLegR = 8
}
export const BONE_COUNT = 9;

export const BONE_NAMES: readonly string[] = [
  'pelvis', 'torso', 'head',
  'upperArmL', 'lowerArmL', 'upperArmR', 'lowerArmR',
  'lowerLegL', 'lowerLegR'
];

export interface BoneSpec {
  name: string;
  type: 'capsule' | 'sphere';
  halfHeight: number;
  radius: number;
  density: number;
}

const C = CONFIG.character;

export const BONE_SPECS: readonly BoneSpec[] = [
  { name: 'pelvis',     type: 'capsule', halfHeight: C.pelvisHalfHeight,    radius: C.pelvisRadius,    density: 1.4 },
  { name: 'torso',      type: 'capsule', halfHeight: C.torsoHalfHeight,     radius: C.torsoRadius,     density: 1.0 },
  { name: 'head',       type: 'sphere',  halfHeight: 0,                     radius: C.headRadius,      density: 0.9 },
  { name: 'upperArmL',  type: 'capsule', halfHeight: C.upperArmHalfHeight,  radius: C.upperArmRadius,  density: 0.8 },
  { name: 'lowerArmL',  type: 'capsule', halfHeight: C.lowerArmHalfHeight,  radius: C.lowerArmRadius,  density: 0.7 },
  { name: 'upperArmR',  type: 'capsule', halfHeight: C.upperArmHalfHeight,  radius: C.upperArmRadius,  density: 0.8 },
  { name: 'lowerArmR',  type: 'capsule', halfHeight: C.lowerArmHalfHeight,  radius: C.lowerArmRadius,  density: 0.7 },
  { name: 'lowerLegL',  type: 'capsule', halfHeight: C.lowerLegHalfHeight,  radius: C.lowerLegRadius,  density: 1.0 },
  { name: 'lowerLegR',  type: 'capsule', halfHeight: C.lowerLegHalfHeight,  radius: C.lowerLegRadius,  density: 1.0 }
];

export function packGroups(membership: number, filter: number): number {
  return ((membership & 0xffff) << 16) | (filter & 0xffff);
}

export interface RagdollHandles {
  bodies: RAPIER.RigidBody[];
  colliders: RAPIER.Collider[];
  joints: RAPIER.ImpulseJoint[];
  elbowL: RAPIER.RevoluteImpulseJoint;
  elbowR: RAPIER.RevoluteImpulseJoint;
  collisionMembership: number;
}

/**
 * Collision bit assignment for one character. Three bits (`torso`, `limb`,
 * `leg`) encode a self-collision scheme designed so nothing splays the
 * default stance AND so same-char legs don't trip each other during turning:
 *
 *  - TORSO ↔ ARM/LEG (same char): ON. An arm leaning across the body now hits
 *    the torso instead of passing through. This works because the torso body
 *    carries TWO colliders — a full-radius "outer" one (identical to the
 *    original) that only opponents and the arena see, plus an "inner" one
 *    (radius ≈ torsoRadius - armRadius) that ONLY this character's own limbs
 *    (arms AND legs) see. At rest the arm axis sits on the shoulder anchor
 *    which is exactly at the inner surface — grazing, no penetration, no splay.
 *  - ARM ↔ ARM (same char): ON. Crossed arms register.
 *  - ARM ↔ LEG (same char): ON. Arm bumping a leg during falls / kicks.
 *  - LEG ↔ LEG (same char): OFF. When turning mid-walk the swing foot would
 *    otherwise catch on the plant foot and trip the character. Arms/legs are
 *    on separate bits so we can drop leg↔leg without also losing arm↔leg.
 *  - PELVIS / HEAD ↔ anything same char: OFF. The hip anchor is ~12 cm
 *    *inside* a pelvis of radius 22 cm, so the leg axis is already deep inside
 *    the pelvis capsule at rest — no filter trick or collider shrink can fix
 *    this without moving the hip anchor outward (which widens the silhouette).
 *    Same story for the head, which lives inside the torso at rest. Pelvis and
 *    head share the `torso` membership bit (their filter is opponent+arena
 *    only, so they never see anything of their own anyway — the bit just needs
 *    to be something opponents can filter on, and opponents already include
 *    every bit of us in their opponent mask).
 *
 * `opponent` is the OR of every OTHER character's three bits. `arena` is the
 * static-world bit.
 */
export interface CharGroups {
  torso: number;
  limb: number;
  leg: number;
  opponent: number;
  arena: number;
}

export class Ragdoll {
  readonly handles: RagdollHandles;
  private readonly world: RAPIER.World;
  private readonly ownColliderHandles: Set<number>;
  /** Spherical-joint anchors captured at construction so the wedge-detector
   *  can compute world-space anchor mismatch without re-deriving constants. */
  private readonly jointSpecs: ReadonlyArray<{
    parent: RAPIER.RigidBody;
    child: RAPIER.RigidBody;
    parentLocal: { x: number; y: number; z: number };
    childLocal: { x: number; y: number; z: number };
  }>;

  constructor(
    world: RAPIER.World,
    spawnX: number,
    spawnZ: number,
    yaw: number,
    groups: CharGroups,
    spawnY?: number
  ) {
    this.world = world;

    const positions = Ragdoll.computeSpawnPositions(spawnX, spawnZ, yaw, spawnY);

    const initRot = quatFromYaw(yaw);

    const bodies: RAPIER.RigidBody[] = [];
    const colliders: RAPIER.Collider[] = [];
    for (let i = 0; i < BONE_SPECS.length; i++) {
      const spec = BONE_SPECS[i]!;
      const p = positions[i]!;
      const bd = RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(p[0], p[1], p[2])
        .setRotation(initRot)
        .setLinearDamping(C.linearDamping)
        .setAngularDamping(C.angularDamping)
        .setCcdEnabled(false);
      const body = world.createRigidBody(bd);
      bodies.push(body);

      const { membership, filter, halfHeight, radius } = resolveCollider(i as Bone, spec, groups);
      const cd = (spec.type === 'sphere'
        ? RAPIER.ColliderDesc.ball(radius)
        : RAPIER.ColliderDesc.capsule(halfHeight, radius)
      )
        .setDensity(spec.density * C.massScale)
        .setFriction(0.6)
        .setRestitution(0.05)
        .setCollisionGroups(packGroups(membership, filter))
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
      const col = world.createCollider(cd, body);
      colliders.push(col);

      // Torso gets a second, inner collider specifically for own-limb self-
      // collision. Same body → they don't collide with each other; different
      // filter → only this character's own limbs (arms AND legs) see the
      // inner one.
      if (i === Bone.Torso) {
        const innerDesc = RAPIER.ColliderDesc.capsule(spec.halfHeight, C.torsoInnerColliderRadius)
          .setDensity(0) // outer collider already carries the mass
          .setFriction(0.6)
          .setRestitution(0.05)
          .setCollisionGroups(packGroups(groups.torso, groups.limb | groups.leg))
          .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
        const innerCol = world.createCollider(innerDesc, body);
        colliders.push(innerCol);
      }
    }

    // ---- Joints ----
    const v = (x: number, y: number, z: number) => ({ x, y, z });
    const joints: RAPIER.ImpulseJoint[] = [];
    const jointSpecs: Array<{
      parent: RAPIER.RigidBody;
      child: RAPIER.RigidBody;
      parentLocal: { x: number; y: number; z: number };
      childLocal: { x: number; y: number; z: number };
    }> = [];
    const addSpherical = (
      parent: RAPIER.RigidBody,
      child: RAPIER.RigidBody,
      parentLocal: { x: number; y: number; z: number },
      childLocal: { x: number; y: number; z: number }
    ) => {
      joints.push(world.createImpulseJoint(
        RAPIER.JointData.spherical(parentLocal, childLocal),
        parent, child, true
      ));
      jointSpecs.push({ parent, child, parentLocal, childLocal });
    };

    // Spine: pelvis ↔ torso (passive spherical)
    addSpherical(
      bodies[Bone.Pelvis]!, bodies[Bone.Torso]!,
      v(0, +C.pelvisHalfHeight + C.pelvisRadius * 0.3, 0),
      v(0, -C.torsoHalfHeight - C.torsoRadius * 0.3, 0)
    );
    // Neck: torso ↔ head. Anchor is held at its pre-shrink world position via
    // torsoNeckAnchorY so the head doesn't drop when the torso gets shorter.
    addSpherical(
      bodies[Bone.Torso]!, bodies[Bone.Head]!,
      v(0, C.torsoNeckAnchorY, 0),
      v(0, -C.headRadius * 0.6, 0)
    );
    // Shoulders — same trick with torsoShoulderAnchorY.
    addSpherical(
      bodies[Bone.Torso]!, bodies[Bone.UpperArmL]!,
      v(-C.torsoRadius * 1.0, C.torsoShoulderAnchorY, 0),
      v(0, +C.upperArmHalfHeight + C.upperArmRadius * 0.4, 0)
    );
    addSpherical(
      bodies[Bone.Torso]!, bodies[Bone.UpperArmR]!,
      v(+C.torsoRadius * 1.0, C.torsoShoulderAnchorY, 0),
      v(0, +C.upperArmHalfHeight + C.upperArmRadius * 0.4, 0)
    );
    // Hips — single spherical joint directly from pelvis to lower-leg stub.
    // The upper leg is gone; the lower leg hangs from a virtual anchor
    // `C.hipToLowerLegLength` above its center. At straight-down rest this
    // puts the lower-leg center at (pelvisHipAnchorWorldY − hipToLowerLegLength),
    // which should equal footCenterHeight so the foot bottom touches the
    // ground. See config.ts for the derivation.
    addSpherical(
      bodies[Bone.Pelvis]!, bodies[Bone.LowerLegL]!,
      v(-C.pelvisRadius * 0.55, -C.pelvisHalfHeight * 0.6, 0),
      v(0, +C.hipToLowerLegLength, 0)
    );
    addSpherical(
      bodies[Bone.Pelvis]!, bodies[Bone.LowerLegR]!,
      v(+C.pelvisRadius * 0.55, -C.pelvisHalfHeight * 0.6, 0),
      v(0, +C.hipToLowerLegLength, 0)
    );

    // Elbows — revolute, axis = local X (which becomes world axis after init yaw rotation)
    const elbowAxis = { x: 1, y: 0, z: 0 };
    const elbowL = world.createImpulseJoint(
      RAPIER.JointData.revolute(v(0, -C.upperArmHalfHeight - C.upperArmRadius * 0.4, 0), v(0, +C.lowerArmHalfHeight + C.lowerArmRadius * 0.4, 0), elbowAxis),
      bodies[Bone.UpperArmL]!, bodies[Bone.LowerArmL]!, true
    ) as RAPIER.RevoluteImpulseJoint;
    const elbowR = world.createImpulseJoint(
      RAPIER.JointData.revolute(v(0, -C.upperArmHalfHeight - C.upperArmRadius * 0.4, 0), v(0, +C.lowerArmHalfHeight + C.lowerArmRadius * 0.4, 0), elbowAxis),
      bodies[Bone.UpperArmR]!, bodies[Bone.LowerArmR]!, true
    ) as RAPIER.RevoluteImpulseJoint;

    elbowL.setLimits(-2.4, 0.05);
    elbowR.setLimits(-2.4, 0.05);

    elbowL.configureMotorModel(RAPIER.MotorModel.AccelerationBased);
    elbowR.configureMotorModel(RAPIER.MotorModel.AccelerationBased);

    joints.push(elbowL, elbowR);

    this.handles = {
      bodies, colliders, joints,
      elbowL, elbowR,
      collisionMembership: groups.torso | groups.limb | groups.leg
    };
    this.ownColliderHandles = new Set(colliders.map(c => c.handle));
    this.jointSpecs = jointSpecs;
  }

  /**
   * Cast a ray straight down from (x, y, z) skipping our own colliders.
   * Returns the distance to the first non-self hit, or null if nothing in
   * range. Used by the foot controller to find where to plant.
   */
  /** Arena-only ground ray. Filters out this character's own colliders AND
   *  every other character's colliders, so "ground" always means world
   *  geometry (arena / ship / ropes / rocks). Otherwise a character walking
   *  *under* you would register as ground — Player B's plant would lock
   *  to Player A's head and the pelvis PD would hoist B up into the air. */
  castGroundRay(x: number, y: number, z: number, maxDist: number): number | null {
    const hit = this.castArenaRayRaw(x, y, z, maxDist);
    return hit == null ? null : hit.timeOfImpact;
  }

  /** Same as `castGroundRay` but also returns the hit body so callers can
   *  attach plant data to moving carriers (a kinematic spaceship that tilts
   *  should carry planted feet with it; a static arena doesn't need the
   *  extra bookkeeping). Returns null on miss. */
  castGroundRayWithBody(x: number, y: number, z: number, maxDist: number): { toi: number; body: RAPIER.RigidBody | null } | null {
    const hit = this.castArenaRayRaw(x, y, z, maxDist);
    if (hit == null) return null;
    return { toi: hit.timeOfImpact, body: hit.collider.parent() };
  }

  /** Find the world Y of the topmost arena collider at (x, z), measured by a
   *  straight-down ray from a Y high above any plausible map geometry. Used
   *  by Character.popToTopmostSurface — the result is the surface to deposit
   *  a wedged ragdoll on top of. Returns null if no arena collider intersects
   *  the cast (player is over the void). */
  castArenaTopSurfaceY(x: number, z: number): number | null {
    const HIGH_Y = 200;
    const MAX_DIST = 400;
    const ray = new RAPIER.Ray({ x, y: HIGH_Y, z }, { x: 0, y: -1, z: 0 });
    const hit = this.world.castRay(
      ray, MAX_DIST, true,
      undefined, undefined, undefined, undefined,
      (c) => {
        if (this.ownColliderHandles.has(c.handle)) return false;
        const groups = c.collisionGroups();
        const membership = (groups >>> 16) & 0xffff;
        return (membership & ~1) === 0;
      }
    );
    if (hit == null) return null;
    return HIGH_Y - hit.timeOfImpact;
  }

  /** True if any bone body's center is buried inside arena geometry. Probes
   *  with a small sphere at each bone's translation, filtered to arena
   *  membership. Catches the case where a limb collider is lodged in a
   *  trimesh feature but the spherical joint can still satisfy its anchor
   *  constraint — that case slips past `maxJointAnchorMismatch`. The probe
   *  radius is tight (0.04 m) so a foot resting on the floor (whose body
   *  center is ~0.27 m above the floor) doesn't trigger. */
  anyBoneInsideArena(): boolean {
    const probe = new RAPIER.Ball(0.04);
    const idRot = { x: 0, y: 0, z: 0, w: 1 };
    let inside = false;
    for (const body of this.handles.bodies) {
      if (inside) break;
      const pos = body.translation();
      this.world.intersectionsWithShape(
        pos, idRot, probe,
        (other) => {
          if (this.ownColliderHandles.has(other.handle)) return true;
          const groups = other.collisionGroups();
          const membership = (groups >>> 16) & 0xffff;
          if ((membership & ~1) === 0) {
            inside = true;
            return false;
          }
          return true;
        }
      );
    }
    return inside;
  }

  /** Largest world-space distance between any spherical joint's two anchor
   *  points. Solver normally keeps these within mm; only an external
   *  contact constraint (e.g. a limb wedged through trimesh geometry) can
   *  hold them visibly apart. Used as the wedge-detection signal. */
  maxJointAnchorMismatch(): number {
    let worst = 0;
    const a = { x: 0, y: 0, z: 0 };
    const b = { x: 0, y: 0, z: 0 };
    for (const spec of this.jointSpecs) {
      const pPos = spec.parent.translation();
      const pRot = spec.parent.rotation();
      const cPos = spec.child.translation();
      const cRot = spec.child.rotation();
      rotateVecByQuat(pRot, spec.parentLocal.x, spec.parentLocal.y, spec.parentLocal.z, a);
      rotateVecByQuat(cRot, spec.childLocal.x, spec.childLocal.y, spec.childLocal.z, b);
      const dx = (pPos.x + a.x) - (cPos.x + b.x);
      const dy = (pPos.y + a.y) - (cPos.y + b.y);
      const dz = (pPos.z + a.z) - (cPos.z + b.z);
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > worst) worst = d2;
    }
    return Math.sqrt(worst);
  }

  /** Translate every bone body uniformly on Y, preserving pose (relative
   *  offsets and rotations stay identical), and zero all linear + angular
   *  velocities so the next solver step doesn't re-launch the body. Used by
   *  Character.popToTopmostSurface for the wedge-recovery teleport. */
  translateAllBonesY(deltaY: number): void {
    for (const b of this.handles.bodies) {
      const t = b.translation();
      b.setTranslation({ x: t.x, y: t.y + deltaY, z: t.z }, true);
      b.setLinvel({ x: 0, y: 0, z: 0 }, true);
      b.setAngvel({ x: 0, y: 0, z: 0 }, true);
    }
  }

  /** Internal: arena-membership-filtered downward ray. Character collider
   *  membership bits are all at index ≥ 1 (bit 0 is reserved for arena-type
   *  objects — ship, ropes, rocks, static floors), so `membership & ~1 === 0`
   *  keeps exactly the world-geometry colliders we want for ground checks. */
  private castArenaRayRaw(x: number, y: number, z: number, maxDist: number) {
    const ray = new RAPIER.Ray({ x, y, z }, { x: 0, y: -1, z: 0 });
    return this.world.castRay(
      ray, maxDist, true,
      undefined, undefined, undefined, undefined,
      (c) => {
        if (this.ownColliderHandles.has(c.handle)) return false;
        const groups = c.collisionGroups();
        const membership = (groups >>> 16) & 0xffff;
        return (membership & ~1) === 0;
      }
    );
  }

  pelvis(): RAPIER.RigidBody { return this.handles.bodies[Bone.Pelvis]!; }
  body(b: Bone): RAPIER.RigidBody { return this.handles.bodies[b]!; }

  /**
   * Build all 11 bone world positions hierarchically so that each joint's two
   * anchors coincide in world space at spawn (zero solver slack on frame 1).
   * Pelvis Y is chosen so the lower-leg bottoms clear the arena floor.
   */
  static computeSpawnPositions(spawnX: number, spawnZ: number, yaw: number, spawnY?: number): [number, number, number][] {
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    // R(yaw) on (x,y,z) → (cos*x + sin*z, y, -sin*x + cos*z)
    const rot = (v: [number, number, number]): [number, number, number] =>
      [cos * v[0] + sin * v[2], v[1], -sin * v[0] + cos * v[2]];
    const sub = (a: [number, number, number], b: [number, number, number]): [number, number, number] =>
      [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
    const add = (a: [number, number, number], b: [number, number, number]): [number, number, number] =>
      [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
    // Position a child bone so its anchor coincides with the parent's anchor in world.
    // childPos + R*childAnchor = parentPos + R*parentAnchor
    // → childPos = parentPos + R*(parentAnchor - childAnchor)
    const child = (
      parentPos: [number, number, number],
      parentAnchor: [number, number, number],
      childAnchor: [number, number, number]
    ): [number, number, number] => add(parentPos, rot(sub(parentAnchor, childAnchor)));

    // Pelvis Y picked so lower-leg bottom clears floor with margin.
    // With the short-upper-leg stub the straight-chain pelvis→lowerLeg drop
    // is ~0.50; spawning at 0.85 lands the lower-leg center near 0.35 and
    // the foot bottom near 0.05 — tiny settle drop, no spawn-penetration.
    // `spawnY` overrides this — used by maps that drop players onto
    // elevated platforms (e.g. the sinking-ship deck).
    const pelvisPos: [number, number, number] = [spawnX, spawnY ?? 0.85, spawnZ];

    // Anchors (mirror what the joint constructors below use)
    const spineP: [number, number, number] = [0, +C.pelvisHalfHeight + C.pelvisRadius * 0.3, 0];
    const spineT: [number, number, number] = [0, -C.torsoHalfHeight - C.torsoRadius * 0.3, 0];
    const neckT:  [number, number, number] = [0, C.torsoNeckAnchorY, 0];
    const neckH:  [number, number, number] = [0, -C.headRadius * 0.6, 0];
    const shoulderT = (s: 1 | -1): [number, number, number] => [s * C.torsoRadius * 1.0, C.torsoShoulderAnchorY, 0];
    const shoulderA: [number, number, number] = [0, +C.upperArmHalfHeight + C.upperArmRadius * 0.4, 0];
    const elbowU: [number, number, number] = [0, -C.upperArmHalfHeight - C.upperArmRadius * 0.4, 0];
    const elbowL: [number, number, number] = [0, +C.lowerArmHalfHeight + C.lowerArmRadius * 0.4, 0];
    const hipP = (s: 1 | -1): [number, number, number] => [s * C.pelvisRadius * 0.55, -C.pelvisHalfHeight * 0.6, 0];
    // Lower-leg anchor is a virtual point `hipToLowerLegLength` above the
    // lower-leg capsule center — hanging from the pelvis hip like a pendulum.
    const hipLo: [number, number, number] = [0, +C.hipToLowerLegLength, 0];

    const torsoPos = child(pelvisPos, spineP, spineT);
    const headPos = child(torsoPos, neckT, neckH);
    const upperArmLPos = child(torsoPos, shoulderT(-1), shoulderA);
    const upperArmRPos = child(torsoPos, shoulderT(+1), shoulderA);
    const lowerArmLPos = child(upperArmLPos, elbowU, elbowL);
    const lowerArmRPos = child(upperArmRPos, elbowU, elbowL);
    const lowerLegLPos = child(pelvisPos, hipP(-1), hipLo);
    const lowerLegRPos = child(pelvisPos, hipP(+1), hipLo);

    return [
      pelvisPos, torsoPos, headPos,
      upperArmLPos, lowerArmLPos, upperArmRPos, lowerArmRPos,
      lowerLegLPos, lowerLegRPos
    ];
  }

  /**
   * PD torque toward a target world-space orientation, scaled by the bone's
   * own inertia so kp behaves as an angular acceleration (rad/s² per rad of
   * error) regardless of how light or heavy the bone is. Critical damping is
   * roughly kd ≈ 2*sqrt(kp).
   *
   * Why inertia (not mass): applyTorqueImpulse divides by inertia internally.
   * If we scale by mass instead, light bones (e.g. ~9g upper arms with
   * I≈1e-4 kg·m²) get astronomical angular accelerations and fly apart.
   */
  applyOrientationTorque(b: Bone, qTarget: Quat, kp: number, kd: number, dt: number): void {
    const body = this.handles.bodies[b]!;
    const q = body.rotation();
    if (!isFiniteQuat(q)) return;
    const inv: Quat = { x: -q.x, y: -q.y, z: -q.z, w: q.w };
    const err = quatMul(qTarget, inv);
    if (err.w < 0) { err.x = -err.x; err.y = -err.y; err.z = -err.z; err.w = -err.w; }
    const sinHalf = Math.hypot(err.x, err.y, err.z);
    const angle = 2 * Math.atan2(sinHalf, Math.max(1e-6, err.w));
    let ax = 0, ay = 0, az = 0;
    if (sinHalf > 1e-6) {
      ax = err.x / sinHalf;
      ay = err.y / sinHalf;
      az = err.z / sinHalf;
    }
    const omega = body.angvel();
    const I = body.principalInertia();
    const Iyaw = Math.abs(I.y);
    const Iperp = Math.max(Math.abs(I.x), Math.abs(I.z));
    // Tightly clamp the per-axis torque impulse. With a typical I≈1e-4, an
    // impulse of 0.05 corresponds to a 500 rad/s velocity change — already
    // huge. Anything bigger destabilises joints and propagates explosions.
    const max = 0.05;
    const tx = clampFinite((kp * ax * angle - kd * omega.x) * Iperp * dt, max);
    const ty = clampFinite((kp * ay * angle - kd * omega.y) * Iyaw * dt, max);
    const tz = clampFinite((kp * az * angle - kd * omega.z) * Iperp * dt, max);
    body.applyTorqueImpulse({ x: tx, y: ty, z: tz }, true);
  }

  setElbowAngle(side: 'L' | 'R', angle: number, stiff: number, damp: number): void {
    const j = side === 'L' ? this.handles.elbowL : this.handles.elbowR;
    j.configureMotorPosition(angle, stiff, damp);
  }

  /**
   * Hard safety net: clamp every bone's linear and angular velocity to sane
   * maxes after each physics step. Prevents any single explosive impulse
   * (PD spike, punch kick, joint resolution glitch) from rocketing a bone
   * across the stage faster than the joint solver can keep up — which is
   * what was causing the "ragdoll fragments fly away" failure mode.
   */
  clampVelocities(maxLin: number, maxAng: number): void {
    // Hot path — runs every tick, every bone. Squared-magnitude early exit
    // skips the `Math.hypot` (and the `setLinvel`/`setAngvel` Rapier round-
    // trip) on the common case where the body is well within bounds. Output
    // is byte-identical to the pre-change version because we only call hypot
    // when we'd have called it anyway.
    const maxLin2 = maxLin * maxLin;
    const maxAng2 = maxAng * maxAng;
    const bodies = this.handles.bodies;
    for (let i = 0; i < bodies.length; i++) {
      const b = bodies[i]!;
      const v = b.linvel();
      const v2 = v.x * v.x + v.y * v.y + v.z * v.z;
      if (v2 > maxLin2) {
        const k = maxLin / Math.sqrt(v2);
        b.setLinvel({ x: v.x * k, y: v.y * k, z: v.z * k }, true);
      }
      const w = b.angvel();
      const w2 = w.x * w.x + w.y * w.y + w.z * w.z;
      if (w2 > maxAng2) {
        const k = maxAng / Math.sqrt(w2);
        b.setAngvel({ x: w.x * k, y: w.y * k, z: w.z * k }, true);
      }
    }
  }

  writeBones(out: Float32Array): void {
    for (let i = 0; i < this.handles.bodies.length; i++) {
      const b = this.handles.bodies[i]!;
      const p = b.translation();
      const q = b.rotation();
      const o = i * 7;
      out[o + 0] = p.x; out[o + 1] = p.y; out[o + 2] = p.z;
      out[o + 3] = q.x; out[o + 4] = q.y; out[o + 5] = q.z; out[o + 6] = q.w;
    }
  }

  /** Full per-bone serialization including velocities. Layout per bone:
   *    [px, py, pz, qx, qy, qz, qw, lvx, lvy, lvz, avx, avy, avz]
   *  (13 floats). Used by the online client for rollback: prediction buffer
   *  snapshots via this, and `loadBonesFull` restores from a server-sent
   *  bones message so the Rapier solver resumes from a matching state.
   *  Restoring position without velocity causes immediate re-divergence on
   *  the next step (article tip U25). */
  writeBonesFull(out: Float32Array): void {
    for (let i = 0; i < this.handles.bodies.length; i++) {
      const b = this.handles.bodies[i]!;
      const p = b.translation();
      const q = b.rotation();
      const lv = b.linvel();
      const av = b.angvel();
      const o = i * 13;
      out[o + 0] = p.x; out[o + 1] = p.y; out[o + 2] = p.z;
      out[o + 3] = q.x; out[o + 4] = q.y; out[o + 5] = q.z; out[o + 6] = q.w;
      out[o + 7] = lv.x; out[o + 8] = lv.y; out[o + 9] = lv.z;
      out[o + 10] = av.x; out[o + 11] = av.y; out[o + 12] = av.z;
    }
  }

  /** Restore every bone from a full 13-floats-per-bone buffer produced by
   *  `writeBonesFull` (on the server side, or on the client's own prediction
   *  buffer during rollback replay). */
  loadBonesFull(src: Float32Array): void {
    for (let i = 0; i < this.handles.bodies.length; i++) {
      const b = this.handles.bodies[i]!;
      const o = i * 13;
      b.setTranslation({ x: src[o + 0]!, y: src[o + 1]!, z: src[o + 2]! }, true);
      b.setRotation({ x: src[o + 3]!, y: src[o + 4]!, z: src[o + 5]!, w: src[o + 6]! }, true);
      b.setLinvel({ x: src[o + 7]!, y: src[o + 8]!, z: src[o + 9]! }, true);
      b.setAngvel({ x: src[o + 10]!, y: src[o + 11]!, z: src[o + 12]! }, true);
    }
  }

  destroy(world: RAPIER.World): void {
    for (const j of this.handles.joints) world.removeImpulseJoint(j, true);
    for (const c of this.handles.colliders) world.removeCollider(c, true);
    for (const b of this.handles.bodies) world.removeRigidBody(b);
  }
}

// ---------- math helpers ----------

export interface Quat { x: number; y: number; z: number; w: number }

export function quatFromYaw(yaw: number): Quat {
  return { x: 0, y: Math.sin(yaw * 0.5), z: 0, w: Math.cos(yaw * 0.5) };
}
export function quatFromAxisAngle(axis: { x: number; y: number; z: number }, angle: number): Quat {
  const s = Math.sin(angle * 0.5);
  return { x: axis.x * s, y: axis.y * s, z: axis.z * s, w: Math.cos(angle * 0.5) };
}
/** Rotate (x, y, z) by quaternion q. Output reuses the provided `out`
 *  object to avoid per-call allocation — the caller is responsible for
 *  consuming it before the next invocation. */
export function rotateVecByQuat(q: Quat, x: number, y: number, z: number, out: { x: number; y: number; z: number }): void {
  // v' = v + 2 * cross(q.xyz, cross(q.xyz, v) + q.w * v)
  const qx = q.x, qy = q.y, qz = q.z, qw = q.w;
  const tx = 2 * (qy * z - qz * y);
  const ty = 2 * (qz * x - qx * z);
  const tz = 2 * (qx * y - qy * x);
  out.x = x + qw * tx + (qy * tz - qz * ty);
  out.y = y + qw * ty + (qz * tx - qx * tz);
  out.z = z + qw * tz + (qx * ty - qy * tx);
}

/** Inverse of `rotateVecByQuat` — rotates by the conjugate quaternion. */
export function rotateVecByInvQuat(q: Quat, x: number, y: number, z: number, out: { x: number; y: number; z: number }): void {
  rotateVecByQuat({ x: -q.x, y: -q.y, z: -q.z, w: q.w }, x, y, z, out);
}

export function quatMul(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z
  };
}
export const QUAT_ID: Quat = { x: 0, y: 0, z: 0, w: 1 };

export interface Vec3 { x: number; y: number; z: number }

/**
 * Build a quaternion from an orthonormal basis given in world coordinates.
 * xAxis/yAxis/zAxis are where the body's local +X/+Y/+Z axes should point
 * in world space. Caller must pass a right-handed orthonormal set
 * (zAxis = xAxis × yAxis).
 */
export function quatFromBasis(xAxis: Vec3, yAxis: Vec3, zAxis: Vec3): Quat {
  // Columns of the rotation matrix R = [X Y Z]: R * localVec = worldVec.
  const m00 = xAxis.x, m10 = xAxis.y, m20 = xAxis.z;
  const m01 = yAxis.x, m11 = yAxis.y, m21 = yAxis.z;
  const m02 = zAxis.x, m12 = zAxis.y, m22 = zAxis.z;
  const trace = m00 + m11 + m22;
  if (trace > 0) {
    const s = Math.sqrt(trace + 1.0) * 2;
    return { x: (m21 - m12) / s, y: (m02 - m20) / s, z: (m10 - m01) / s, w: 0.25 * s };
  } else if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1.0 + m00 - m11 - m22) * 2;
    return { x: 0.25 * s, y: (m01 + m10) / s, z: (m02 + m20) / s, w: (m21 - m12) / s };
  } else if (m11 > m22) {
    const s = Math.sqrt(1.0 + m11 - m00 - m22) * 2;
    return { x: (m01 + m10) / s, y: 0.25 * s, z: (m12 + m21) / s, w: (m02 - m20) / s };
  } else {
    const s = Math.sqrt(1.0 + m22 - m00 - m11) * 2;
    return { x: (m02 + m20) / s, y: (m12 + m21) / s, z: 0.25 * s, w: (m10 - m01) / s };
  }
}

/**
 * Outer-collider groups + halfHeight per bone.
 *
 * - TORSO (outer collider): collides with opponent + arena (full visual radius
 *   preserved). An extra INNER collider is created separately on the torso
 *   body to handle own-limb contact — see the main construction loop.
 * - PELVIS / HEAD: collides with opponent + arena only. Shares the `torso`
 *   membership bit; that bit isn't in their own filter so they never collide
 *   with own torso/limbs. The hip anchor is buried inside the pelvis and the
 *   head sits inside the torso, so any self-collision here would splay limbs
 *   / launch the head.
 * - ARM (upper/lower): membership `limb`. Filter = opponent + arena + own
 *   LIMB + own LEG + own TORSO. Arm vs own arm ON, arm vs own leg ON, arm
 *   vs own torso ON (via inner collider).
 * - LEG (upper/lower): membership `leg`. Filter = opponent + arena + own
 *   LIMB + own TORSO (NO `leg` — so same-char leg↔leg is OFF: the swing
 *   foot can pass through the plant foot while turning mid-walk instead of
 *   tripping). Arms still see legs because arms' filter includes `leg`.
 *   Same-arm upper↔lower and same-leg upper↔lower would otherwise overlap at
 *   the elbow / knee hemispheres — the halfHeight scale pulls those caps
 *   inside the joint anchor so there's nothing to resolve at rest (moot for
 *   same-side leg overlap now that leg↔leg is off entirely, but we keep the
 *   length scale so the shrunken capsule still doesn't catch on opponent or
 *   arena edges).
 */
function resolveCollider(
  bone: Bone,
  spec: BoneSpec,
  g: CharGroups
): { membership: number; filter: number; halfHeight: number; radius: number } {
  const opponentAndArena = g.opponent | g.arena;
  switch (bone) {
    case Bone.Torso:
      return { membership: g.torso, filter: opponentAndArena, halfHeight: spec.halfHeight, radius: spec.radius };
    case Bone.Pelvis:
    case Bone.Head:
      return { membership: g.torso, filter: opponentAndArena, halfHeight: spec.halfHeight, radius: spec.radius };
    case Bone.UpperArmL: case Bone.UpperArmR:
    case Bone.LowerArmL: case Bone.LowerArmR:
      return {
        membership: g.limb,
        filter: opponentAndArena | g.limb | g.leg | g.torso,
        halfHeight: spec.halfHeight * C.armColliderLengthScale,
        radius: spec.radius
      };
    case Bone.LowerLegL: case Bone.LowerLegR:
      return {
        membership: g.leg,
        filter: opponentAndArena | g.limb | g.torso,
        halfHeight: spec.halfHeight * C.legColliderLengthScale,
        radius: spec.radius * C.legColliderRadiusScale
      };
  }
}

function isFiniteQuat(q: Quat): boolean {
  return Number.isFinite(q.x) && Number.isFinite(q.y) && Number.isFinite(q.z) && Number.isFinite(q.w);
}
function clampFinite(v: number, max: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v > max) return max;
  if (v < -max) return -max;
  return v;
}
